/**
 * @component ContentProvider
 * @description 주입된 콘텐츠를 제공하고 주입이 없으면 한 번 조회합니다.
 * @usage <ContentProvider><App /></ContentProvider>
 */

import { useEffect, useMemo, useState } from 'react';
import { fetchContent } from '../services/adminApi';
import { ContentContext } from './ContentContext';
import { flattenContentValues, hasInjectedContent, readInjectedValues } from './contentSource';

// [기능] 별도 DOM 없이 현재 콘텐츠 값 맵을 자식에게 제공합니다.
export default function ContentProvider({ children }) {
  // [기술] 초기화 함수 참조를 전달해 매 렌더마다 주입 전역을 다시 읽지 않습니다.
  const [values, setValues] = useState(readInjectedValues);

  // [기능] 주입이 없을 때 콘텐츠를 조회하고 언마운트 이후 갱신을 막습니다.
  useEffect(() => {
    // [비즈니스] 운영의 빈 주입 맵도 정상 상태이므로 추가 요청하지 않습니다.
    if (hasInjectedContent()) return undefined;
    let isActive = true;

    // [기능] 조회한 저장 레코드를 평탄화해 활성 Provider에 반영합니다.
    async function loadContent() {
      try {
        const record = await fetchContent();
        if (isActive) setValues(flattenContentValues(record));
      } catch {
        // [비즈니스] 조회 실패 시 코드 기본값을 유지해 공개 페이지가 계속 표시되도록 합니다.
      }
    }
    loadContent();

    // [기능] 늦게 도착한 응답이 언마운트된 Provider를 갱신하지 않도록 합니다.
    return () => { isActive = false; };
  }, []);

  // [기능] 값 맵이 바뀔 때만 컨텍스트 전달 객체를 갱신합니다.
  // [기술] 참조를 유지해 불필요한 소비자 재렌더를 피합니다.
  const contextValue = useMemo(() => ({ values }), [values]);
  return <ContentContext.Provider value={contextValue}>{children}</ContentContext.Provider>;
}
